import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import { TagInfo } from '../../../interfaces';
import { getTransformedDataForPagination, LocalStorageKeys, objectToQueryParams } from '../../../utils';

export interface UserAssignment {
    _id: string; // Must follow pattern: ^[a-f\d]{24}$
    created_time?: string;
    created_user?: string;
    created_user_id?: string;
    modified_time?: string;
    modified_user?: string;
    modified_user_id?: string;
    assigned_user_email?: string;
    assigned_user_id: string;
    assigned_user_name?: string;
    followers?: string[];
    notes: string;
    object_id: string;
    object_type: string;
    workflow_tag?: Partial<TagInfo>;
    history?: UserAssignment[];
}

interface UserAssignmentGetParams {
    created_user?: string;
    assigned_user?: string;
    object_id?: string;
    object_type?: string;
    include_histories?: boolean;
    tag_id?: string;
    page_no?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
}

interface SuccessResponse {
    sequence_id?: string;
    version?: number;
    _id: string;
    message: string;
    url?: string;
}

export const userAssignmentAPI = createApi({
    reducerPath: "userAssignmentAPI",
    baseQuery: retry(fetchBaseQuery({
        baseUrl: config.api_url + "user_assignment",
        prepareHeaders: (headers) => {
            const token = localStorage.getItem(LocalStorageKeys.authToken);
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }), { maxRetries: 1 }),
    endpoints: (builder) => ({
        getUserAssignments: builder.query<{ data: UserAssignment[], totalCount: number }, UserAssignmentGetParams>({
            query: (params) => ({ url: objectToQueryParams(params), method: "GET" }),
            transformResponse: (data: UserAssignment[], meta: { request: Request; response?: Response }) => {
                return getTransformedDataForPagination(data, meta?.response);
            },
        }),
        getUserAssignmentById: builder.query<UserAssignment, { id: string; include_histories?: boolean }>({
            query: ({ id, include_histories }) => ({ url: `/${id}?include_histories=${include_histories ?? false}`, method: "GET" }),
        }),
        createUserAssignment: builder.mutation<SuccessResponse, Partial<UserAssignment>>({
            query: (payload) => ({ method: "POST", body: payload }),
        }),
        deleteUserAssignment: builder.mutation<string, string>({
            query: (id) => ({ url: `/${id}`, method: "DELETE" }),
        }),
    })
});

export const {
    useGetUserAssignmentsQuery,
    useLazyGetUserAssignmentsQuery,
    useGetUserAssignmentByIdQuery,
    useLazyGetUserAssignmentByIdQuery,
    useCreateUserAssignmentMutation,
    useDeleteUserAssignmentMutation,
} = userAssignmentAPI;
